// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TEventMap = Record<string, any>;
export type TEventKey<T extends TEventMap> = string & keyof T;
export type TEventReceiver<T> = (params: T) => unknown;
export type TFun = () => unknown;

export interface IEmitter<T extends TEventMap> {
  on<K extends TEventKey<T>>(eventName: K, fn: TEventReceiver<T[K]>): TFun;
  off<K extends TEventKey<T>>(eventName: K, fn: TEventReceiver<T[K]>): unknown;
  emit<K extends TEventKey<T>>(eventName: K, params?: T[K]): unknown;
}

export function eventEmitter<T extends TEventMap>(): IEmitter<T> {
  const listeners: {
    [K in keyof TEventMap]?: Array<(p: TEventMap[K]) => unknown>;
  } = {};

  return {
    on(key, fn) {
      if (!Array.isArray(listeners[key])) listeners[key] = [];
      listeners[key]?.push(fn);
      return () => {
        this.off(key, fn);
      };
    },
    off(key, fn) {
      listeners[key] = (listeners[key] || []).filter((cb) => cb !== fn);
    },
    emit(key, data) {
      (listeners[key] || []).forEach((fn) => fn(data));
    },
  };
}

/**
 * EXISTE UN EVENT EMITTER EN npm QUE ES MANTENIDO POR LA COMUNIDAD
 *
 * Se decidió mantener éste ya que al momento de tomar la decisión, ofrecía
 * un tipeado más sólido (aunque usted no lo crea).
 *
 * Su funcionamiento es muy sencillo, se instancia la clase EventEmitter
 * pasándole como tipo un objeto que contenga la definición de los eventos y
 * el tipo del valor entregado por cada evento. Luego es posible llamar
 * a los métodos on y off de la instancia para ser notificado de un suceso
 * o al método emit para notificar a los suscriptores.
 *
 * @example
 *
 * const clicksAnnouncer = new EventEmitter<{ clicks: number }>();
 *
 * let clicks = 0;
 * document.addEventListener('click', ()=> {
 *   const clicksAmmount = ++clicks;
 *   clicksAnnouncer.emit('clicks', clicksAmmount);
 * })
 *
 * clicksAnnouncer.on('clicks', (clicksAmmount) =>
 *    console.log(`Se han realizado ${clicksAmmount} clicks.`));
 */
export default class EventEmitter<T extends TEventMap> implements IEmitter<T> {
  protected emitter = eventEmitter<T>();

  on<K extends TEventKey<T>>(eventName: K, fn: TEventReceiver<T[K]>) {
    this.emitter.on(eventName, fn);
    return () => {
      this.emitter.off(eventName, fn);
    };
  }

  off<K extends TEventKey<T>>(eventName: K, fn: TEventReceiver<T[K]>) {
    this.emitter.off(eventName, fn);
  }

  emit<K extends TEventKey<T>>(eventName: K, params?: T[K]) {
    this.emitter.emit(eventName, params);
  }
}
